/* eslint-disable @typescript-eslint/ban-types */
import React, { ButtonHTMLAttributes, FC, ReactNode } from 'react';
import { styled } from 'linaria/react';
import { defaultTheme, withTheme } from '@shared/lib/theme';
import { buttonBackgroundColorByVariant, buttonBorderRadiusesRem, buttonFontSizesRem, buttonIconSizesRem, buttonLineHeightsRem, buttonPaddingsRem, buttonSpinnerSizesRem, buttonFontSizesPx, buttonLineHeightsPx, buttonPaddingsPx, buttonBorderRadiusesPx, buttonSpinnerSizesPx, buttonIconSizesPx, buttonTextColorByVariant } from '@shared/config/components/button';
import { Spinner } from '../../atoms';
type SizeValue = keyof typeof buttonFontSizesRem;
type ResponsiveSize = SizeValue | {
  mobile: SizeValue;
  desktop: SizeValue;
};
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  color?: string & {};
  textColor?: string & {};
  variant?: 'primary' | 'secondary' | 'tertiary' | 'transparent';
  disabled?: boolean;
  isLoading?: boolean;
  isInactive?: boolean;
  withHoverShadow?: boolean;
  family?: keyof typeof defaultTheme.typography.family;
  size?: ResponsiveSize;
  transform?: 'uppercase' | 'lowercase' | 'none';
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  onClick?: () => void;
}
const Button: FC<ButtonProps> = ({
  type = 'button',
  color = '',
  textColor = '',
  variant = 'primary',
  size = '52',
  family = 'MTSWide',
  transform = 'uppercase',
  isLoading = false,
  disabled = false,
  isInactive = false,
  withHoverShadow = false,
  startIcon,
  endIcon,
  onClick,
  children,
  ...rest
}) => {
  const desktopSize = typeof size === 'object' ? size.desktop || '52' : size || '52';
  const mobileSize = typeof size === 'object' ? size.mobile || desktopSize : size || '52';
  const isNonInteractive = disabled || isLoading || isInactive;
  return <ButtonWrapper type={type} color={color} textColor={textColor} variant={variant} family={family} desktopSize={desktopSize} mobileSize={mobileSize} transform={transform} isLoading={isLoading} isInactive={isInactive} isVisuallyDisabled={disabled} disabled={isNonInteractive} withHoverShadow={withHoverShadow} onClick={onClick} {...rest}>
      {isLoading ? <SpinnerWrapper desktopSize={desktopSize} mobileSize={mobileSize}>
          <Spinner />
        </SpinnerWrapper> : <>
          {startIcon && <IconWrapper isVisuallyDisabled={disabled} desktopSize={desktopSize} mobileSize={mobileSize}>
              {startIcon}
            </IconWrapper>}
          {children}
        </>}
      {endIcon && <IconWrapper isVisuallyDisabled={disabled} desktopSize={desktopSize} mobileSize={mobileSize}>
          {endIcon}
        </IconWrapper>}
    </ButtonWrapper>;
};
export default Button;
type ButtonWrapperProps = Required<Pick<ButtonProps, 'family' | 'transform' | 'variant' | 'color' | 'textColor' | 'isLoading' | 'isInactive' | 'withHoverShadow'>> & {
  desktopSize: SizeValue;
  mobileSize: SizeValue;
  isVisuallyDisabled: boolean;
};
const _exp = /*#__PURE__*/() => ({
  family,
  theme
}) => theme.typography.family[family];
const _exp2 = /*#__PURE__*/() => ({
  desktopSize
}) => buttonFontSizesRem[desktopSize];
const _exp3 = /*#__PURE__*/() => ({
  desktopSize
}) => buttonLineHeightsRem[desktopSize];
const _exp4 = /*#__PURE__*/() => ({
  theme
}) => theme.typography.weight.bold;
const _exp7 = /*#__PURE__*/() => ({
  desktopSize
}) => buttonPaddingsRem[desktopSize];
const _exp8 = /*#__PURE__*/() => ({
  desktopSize
}) => buttonBorderRadiusesRem[desktopSize];
const _exp9 = /*#__PURE__*/() => ({
  transform
}) => transform;
const _exp0 = /*#__PURE__*/() => ({
  isVisuallyDisabled,
  variant,
  color,
  theme
}) => isVisuallyDisabled ? theme.colors.background.disabled : color || buttonBackgroundColorByVariant(variant);
const _exp1 = /*#__PURE__*/() => ({
  isVisuallyDisabled,
  variant,
  textColor,
  theme
}) => isVisuallyDisabled ? theme.colors.text.tertiary : textColor || buttonTextColorByVariant(variant);
const _exp10 = /*#__PURE__*/() => ({
  isVisuallyDisabled,
  isLoading,
  isInactive
}) => isVisuallyDisabled || isLoading || isInactive ? 'default' : 'pointer';
const _exp11 = /*#__PURE__*/() => ({
  isVisuallyDisabled,
  isLoading,
  isInactive
}) => isVisuallyDisabled || isLoading || isInactive ? 'none' : 'default';
const _exp12 = /*#__PURE__*/() => ({
  withHoverShadow,
  theme
}) => withHoverShadow ? `0px 0px 2.5rem 1.125rem ${theme.colors.background.accent_30}` : `inset 0 0 0 9999px ${theme.colors.background.hover}`;
const _exp13 = /*#__PURE__*/() => ({
  withHoverShadow,
  theme
}) => withHoverShadow ? `0px 0px 2.5rem 1.125rem ${theme.colors.background.accent_30}` : `inset 0 0 0 9999px ${theme.colors.background.hover}`;
const _exp14 = /*#__PURE__*/() => ({
  mobileSize
}) => buttonFontSizesPx[mobileSize];
const _exp15 = /*#__PURE__*/() => ({
  mobileSize
}) => buttonLineHeightsPx[mobileSize];
const _exp18 = /*#__PURE__*/() => ({
  mobileSize
}) => buttonPaddingsPx[mobileSize];
const _exp19 = /*#__PURE__*/() => ({
  mobileSize
}) => buttonBorderRadiusesPx[mobileSize];
export const ButtonWrapper = withTheme(/*#__PURE__*/styled('button')({
  name: "ButtonWrapper",
  class: "bawvt7j",
  propsAsIs: false,
  vars: {
    "bawvt7j-0": [_exp()],
    "bawvt7j-1": [_exp2()],
    "bawvt7j-2": [_exp3()],
    "bawvt7j-3": [_exp4()],
    "bawvt7j-4": [_exp7()],
    "bawvt7j-5": [_exp8()],
    "bawvt7j-6": [_exp9()],
    "bawvt7j-7": [_exp0()],
    "bawvt7j-8": [_exp1()],
    "bawvt7j-9": [_exp10()],
    "bawvt7j-10": [_exp11()],
    "bawvt7j-11": [_exp12()],
    "bawvt7j-12": [_exp13()],
    "bawvt7j-13": [_exp14()],
    "bawvt7j-14": [_exp15()],
    "bawvt7j-15": [_exp18()],
    "bawvt7j-16": [_exp19()]
  }
}));
type IconWrapperPropsType = {
  isVisuallyDisabled: boolean;
  desktopSize: SizeValue;
  mobileSize: SizeValue;
};
const _exp20 = /*#__PURE__*/() => ({
  desktopSize
}) => buttonIconSizesRem[desktopSize];
const _exp21 = /*#__PURE__*/() => ({
  isVisuallyDisabled,
  theme
}) => isVisuallyDisabled ? theme.colors.icon.tertiary : 'inherit';
const _exp22 = /*#__PURE__*/() => ({
  mobileSize
}) => buttonIconSizesPx[mobileSize];
const IconWrapper = withTheme(/*#__PURE__*/styled('div')({
  name: "IconWrapper",
  class: "i1vo0nan",
  propsAsIs: false,
  vars: {
    "i1vo0nan-0": [_exp20()],
    "i1vo0nan-1": [_exp21()],
    "i1vo0nan-2": [_exp22()]
  }
}));
type SpinnerWrapperPropsType = {
  desktopSize: SizeValue;
  mobileSize: SizeValue;
};
const _exp23 = /*#__PURE__*/() => ({
  desktopSize
}) => buttonSpinnerSizesRem[desktopSize];
const _exp24 = /*#__PURE__*/() => ({
  mobileSize
}) => buttonSpinnerSizesPx[mobileSize];
const SpinnerWrapper = /*#__PURE__*/styled('div')({
  name: "SpinnerWrapper",
  class: "s19huits",
  propsAsIs: false,
  vars: {
    "s19huits-0": [_exp23()],
    "s19huits-1": [_exp24()]
  }
});

require("./Button.linaria.module.css!=!../../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./Button.tsx");